import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { has } from 'ramda';
import { messages as enMessages } from './locales/en/messages';
import { messages as esMessages } from './locales/es/messages';
import { messages as frMessages } from './locales/fr/messages';
import { messages as ruMessages } from './locales/ru/messages';
import { messages as ptMessages } from './locales/pt/messages';
import { messages as trMessages } from './locales/tr/messages';
import { messages as grMessages } from './locales/gr/messages';

// Define the available translations
const catalogs: Record<string, any> = {
  en: enMessages,
  es: esMessages,
  fr: frMessages,
  ru: ruMessages,
  pt: ptMessages,
  tr: trMessages,
  gr: grMessages,
};

// IMPORTANT: use lingui's global `i18n` instance rather than a private `setupI18n()` one.
// Many components import `i18n` directly from '@lingui/core'; if the app activated a separate
// instance, those calls would never see the loaded catalogs and would render the English ids.
i18n.load({ en: catalogs.en });
i18n.activate('en'); // Default language

// Function to switch languages dynamically
export const loadLocale = (locale: string) => {
  if (catalogs[locale]) {
    i18n.load({ [locale]: catalogs[locale] });
    i18n.activate(locale);
  }
};

export const DEFAULT_LOCALE = 'en';

const getLocaleStorageKey = (userId: string) => `${userId}-locale`;

export const loadStoredLocale = (userId: string): string => {
  try {
    const stored = window.localStorage.getItem(getLocaleStorageKey(userId));
    return stored !== null && has(stored, catalogs) ? stored : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
};

export const storeLocale = (userId: string, locale: string) => {
  try {
    window.localStorage.setItem(getLocaleStorageKey(userId), locale);
  } catch {
    // localStorage unavailable — the choice simply won't persist
  }
};

// The Software Guide (GuideApp) is built once per UI language under the same locale codes:
// English at /documentation, every other language at /documentation/<locale>.
export const getDocumentationPath = (locale: string) => {
  const isTranslated = locale !== DEFAULT_LOCALE && has(locale, catalogs);
  return isTranslated ? `/documentation/${locale}/docs` : '/documentation/docs';
};

export { i18n, I18nProvider };
